<template>
  <div class="blog-index">
    <div class="top">
      <p class="headline">{{ headline }}</p>
      <div class="filters">
        <ul
          class="filters__list"
          :class="{ 'filters__list--open': filterOpen }"
        >
          <li
            class="filters__filter"
            :class="{ 'filters__filter--active': data.current_tags === null }"
          >
            <a :href="allUrl" @click.prevent="ajaxLoad(allUrl)">View All</a>
          </li>
          <li
            v-for="filter in filters"
            :key="filter.handle"
            class="filters__filter"
            :class="{
              'filters__filter--active':
                data.current_tags && data.current_tags.includes(filter.handle),
            }"
          >
            <a :href="filter.url" @click.prevent="ajaxLoad(filter.url)">{{
              filter.title
            }}</a>
          </li>
        </ul>
        <button class="filters__button" @click="filterOpen = !filterOpen">
          Filter
          <span v-if="filterOpen"><i class="fal fa-chevron-up"/></span>
          <span v-else><i class="fal fa-chevron-down"/></span>
        </button>
      </div>
      <div
        class="search"
        :class="{ 'search--focus': searchFocus || searchQuery }"
        v-if="search"
      >
        <label class="search__label" for="search-input">Search the blog</label>
        <span class="search__icon"><i class="fal fa-search"></i></span>
        <input
          class="search__input"
          type="text"
          v-model="searchQuery"
          @focus="searchFocus = true"
          @blur="searchFocus = false"
          @keydown.esc="searchQuery = ''"
        />
      </div>
    </div>

    <div class="grid" :class="{ 'grid--loading': ajaxLoading }">
      <div class="article" v-for="article in data.articles" :key="article.id">
        <a :href="article.url" class="article-preview__link">
          <div class="article-preview__thumbnail">
            <div class="article-preview__tags">
              <div
                class="article-preview__tags__tag"
                v-for="tag in filterTags(article.tags)"
                :key="tag"
              >
                {{ tag }}
              </div>
            </div>
            <img class="article-preview__image" :src="article.image" />
          </div>
          <h4 class="article-preview__title">{{ article.title }}</h4>
          <time
            class="article-preview__time"
            :datetime="article.date"
            v-if="!ambassadors"
          >
            {{ article.published_at }}
          </time>
          <div class="article-preview__social" v-else>
            <a
              class="article-preview__social__link"
              v-for="item in socialLinks(article.social)"
              :key="item.url"
              target="_blank"
              :href="item.url"
            >
              <i :class="item.icon"></i>
            </a>
          </div>
        </a>
      </div>
    </div>

    <ul class="pagination" v-if="data.pagination">
      <li
        v-for="part in data.pagination.parts"
        :key="part.title"
        class="pagination__part"
        :class="{
          'pagination__part--active':
            part.title === data.pagination.current_page,
        }"
      >
        <a
          :href="part.url"
          @click.prevent="ajaxLoad(part.url)"
          v-html="part.title"
          v-if="part.is_link"
        />
        <span v-html="part.title" v-else />
      </li>
    </ul>
  </div>
</template>

<script>
import shopifyAjax from "../mixins/shopifyAjax";
import { debounce } from "../util";

function removeParamFromUrl(key, url) {
  const [cleanUrl, paramsRaw] = url.split("?");
  if (!paramsRaw || paramsRaw === "") return cleanUrl;
  const params = paramsRaw.split("&");
  return params.reduce((acc, param, index) => {
    const [paramKey, value] = param.split("=");
    if (paramKey === key) return acc;
    return `${acc}${paramKey}=${value}${index < params.length - 1 ? "&" : ""}`;
  }, `${cleanUrl}?`);
}

const keyToIcon = (key) => {
  switch (key) {
    case "facebook":
      return "fab fa-facebook-f";
    case "website":
      return "fal fa-globe";
    default:
      return `fab fa-${key}`;
  }
};

export default {
  mixins: [shopifyAjax],
  name: "BlogIndex",
  selector: "#vue-blog-index",
  props: {
    initialData: {
      type: Object,
      required: true,
    },
    filters: {
      type: Array,
      required: true,
    },
    allUrl: {
      type: String,
      required: true,
    },
    headline: {
      type: String,
      required: true,
    },
    ambassadors: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchFocus: false,
      filterOpen: false,
      data: { ...this.initialData },
      searchQuery: "",
    };
  },
  methods: {
    filterTags(tags) {
      return tags.reduce((tags, tag) => {
        const [filter, ...rest] = tag.split("-");
        if (filter !== "filter") return tags;
        return [...tags, rest.join(" ")];
      }, []);
    },
    socialLinks(social) {
      if (!social) return [];
      return Object.keys(social).reduce((acc, key) => {
        const value = social[key];
        if (!value) return acc;
        return [
          ...acc,
          {
            url: value,
            icon: keyToIcon(key),
          },
        ];
      }, []);
    },
    searchFor: debounce(async function(query) {}, 200),
  },
  watch: {
    searchQuery: debounce(async function() {
      if (this.searchQuery.length < 2) {
        this.data = { ...this.initialData };
      } else {
        try {
          const { data } = await this.$http(
            `/search?view=ajax&q=${this.searchQuery}*&type=article`
          );
          this.data.articles = data.filter((article) => !article.ambassador);
        } finally {
        }
      }
    }, 200),
  },
};
</script>

<style lang="scss" scoped>
.collection {
  @include inner-container();
}

.top {
  margin-top: 64px;
  display: flex;
  align-items: center;

  @include media("<=tablet") {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
}

.headline {
  font-weight: 700;
  margin: 0;
  font-size: 21px;
}

.filters {
  margin-left: 48px;
  font-size: 14px;
  flex: 1;

  @include media("<=tablet") {
    margin-left: 0;
  }

  &__button {
    display: none;

    @include media("<=tablet") {
      @include button-reset();
      margin-top: 32px;
      display: inline-block;
      font-size: 14px;
    }
  }

  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;

    @include media("<=tablet") {
      margin-top: 32px;
      display: none;
      &--open {
        display: block;
      }
    }
  }

  &__filter {
    a {
      color: $black;
      text-decoration: none;
    }

    &--active {
      font-weight: 700;
    }

    & + & {
      margin-left: 16px;

      @include media("<=tablet") {
        margin-left: 0;
        margin-top: 16px;
      }
    }
  }
}

.search {
  width: 236px;
  margin-left: 16px;
  position: relative;

  &__input {
    appearance: none;
    background-color: transparent;
    border: none;
    width: 100%;
    border-bottom: 1px solid black;
    font-size: 16px;
    font-family: $font-family;
    padding-left: 24px;
    padding-bottom: 8px;
    outline: none;
  }

  &__icon {
    pointer-events: none;
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(calc(-50% - 4px));
  }

  &__label {
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform-origin: center left;
    transform: translate(24px, calc(-50% - 4px));
    transition: transform 0.2s ease-out;

    .search--focus & {
      transform: scale(0.8) translate(0, -280%);
    }
  }
}

.grid {
  margin-top: 56px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-template-rows: auto auto auto auto;
  grid-column-gap: 28px;
  grid-row-gap: 48px;
  transition: opacity 0.2s ease-out;

  &--loading {
    opacity: 0;
  }

  @include media("<=tablet") {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: auto auto auto auto auto;
    grid-column-gap: 16px;
  }

  @include media("<=phone") {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: repeat(8 auto);
    grid-column-gap: 16px;
  }
}

.pagination {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  justify-content: center;

  &__part {
    border-radius: 4px;
    background-color: transparent;
    padding: 8px 12px;
    font-size: 20px;
    transition: background-color 0.15s ease-in-out;

    & + & {
      margin-left: 16px;
    }

    a {
      color: $black;
      text-decoration: none;
    }

    &:hover {
      background-color: rgba($teal, 0.1);
    }
    &--active {
      background-color: $teal;
      color: $white;
      &:hover {
        background-color: $teal;
      }
    }
  }
}
</style>
